'use client';

import { Icon, Image } from '@petsmart-ui/sparky';
import { useEffect, useRef, useState } from 'react';

import { decodeEntityString } from '@libs/algolia';
import { useDecisions } from '@libs/common-next';

import type { OptexSearchBiaGuestProduct } from './use-optex-search-bia-guest';
import './optex-search-bia-guest.scss';

type OptexSearchBiaGuestProps = Readonly<{
    query: string;
    products?: OptexSearchBiaGuestProduct[] | null;
}>;

const OPTEX_SEARCH_BIA_GUEST_SCROLL_DISTANCE = 312;

export function OptexSearchBiaGuest({ query, products }: OptexSearchBiaGuestProps) {
    const isOptexSearchBiaGuestEnabled = useDecisions('search_bia_guest');
    const optexRailRef = useRef<HTMLUListElement>(null);
    const [canScrollBack, setCanScrollBack] = useState(false);
    const [canScrollForward, setCanScrollForward] = useState(false);

    useEffect(() => {
        const optexRail = optexRailRef.current;
        if (!optexRail) return;

        const updateOptexScrollControls = () => {
            setCanScrollBack(optexRail.scrollLeft > 0);
            setCanScrollForward(optexRail.scrollLeft + optexRail.clientWidth < optexRail.scrollWidth - 1);
        };

        updateOptexScrollControls();
        optexRail.addEventListener('scroll', updateOptexScrollControls);
        window.addEventListener('resize', updateOptexScrollControls);
        return () => {
            optexRail.removeEventListener('scroll', updateOptexScrollControls);
            window.removeEventListener('resize', updateOptexScrollControls);
        };
    }, [products, isOptexSearchBiaGuestEnabled, query]);

    if (!isOptexSearchBiaGuestEnabled || query !== '' || !products?.length) return null;

    return (
        <section className="optex-search-bia-guest" aria-labelledby="optex-search-bia-guest-title">
            <h2 className="optex-search-bia-guest__title" id="optex-search-bia-guest-title">
                Most-loved essentials
            </h2>
            <div className="optex-search-bia-guest__carousel">
                <ul
                    ref={optexRailRef}
                    className="optex-search-bia-guest__rail"
                    aria-label="Most-loved essentials products"
                >
                    {products.map((optexProduct, index) => (
                        <li className="optex-search-bia-guest__item" key={`${optexProduct.sku}-${index}`}>
                            <a className="optex-search-bia-guest__link" href={optexProduct.href}>
                                <Image
                                    className="optex-search-bia-guest__image"
                                    src={optexProduct.imageUrl}
                                    alt=""
                                    width={96}
                                    height={96}
                                    loading="lazy"
                                />
                                <span className="optex-search-bia-guest__name">
                                    {decodeEntityString(optexProduct.name)}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
                {canScrollBack && (
                    <button
                        aria-label="Previous Most-loved essentials products"
                        className="optex-search-bia-guest__arrow optex-search-bia-guest__arrow--back"
                        type="button"
                        onClick={() =>
                            optexRailRef.current?.scrollBy({
                                left: -OPTEX_SEARCH_BIA_GUEST_SCROLL_DISTANCE,
                                behavior: 'smooth',
                            })
                        }
                    >
                        <Icon name="chevron-left" />
                    </button>
                )}
                {canScrollForward && (
                    <button
                        aria-label="Next Most-loved essentials products"
                        className="optex-search-bia-guest__arrow optex-search-bia-guest__arrow--forward"
                        type="button"
                        onClick={() =>
                            optexRailRef.current?.scrollBy({
                                left: OPTEX_SEARCH_BIA_GUEST_SCROLL_DISTANCE,
                                behavior: 'smooth',
                            })
                        }
                    >
                        <Icon name="chevron-right" />
                    </button>
                )}
            </div>
        </section>
    );
}
